import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  LogIn, Eye, Users, Activity, Monitor, Smartphone, Tablet, Globe, Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const CHART_PALETTE = ['#1a1a1a', '#e8590c', '#b0b0b0', '#f0976c', '#666666', '#d4d4d4'];

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

const getPeriods = (t) => [
  { value: 'today', label: t('today') },
  { value: 'week', label: t('sevenDays') },
  { value: 'month', label: t('thirtyDays') },
];

const PlatformMonitoringPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');

  useEffect(() => {
    if (user?.role !== 'platform_admin') {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [period, user]);

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/monitoring/stats', { params: { period } });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching monitoring stats:', err);
      setError(err.response?.data?.detail || t('statsLoadError'));
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'platform_admin') return null;

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="analytics-loading">
          <div className="analytics-skeleton" style={{ height: 32, width: 280, marginBottom: 12 }} />
          <div className="analytics-skeleton" style={{ height: 20, width: 400, marginBottom: 40 }} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="analytics-skeleton" style={{ height: 120, borderRadius: 8 }} />
            ))}
          </div>
        </div>
      </ProfessionalLayout>
    );
  }

  if (error) {
    return (
      <ProfessionalLayout>
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </ProfessionalLayout>
    );
  }

  const totals = data?.totals || {};
  const activeUsers = data?.active_users || {};
  const sessions = data?.sessions || {};

  const kpis = [
    { label: t('logins'), value: totals.total_logins || 0, sub: `${activeUsers.dau || 0} ${t('today').toLowerCase()}`, icon: LogIn, color: '#e8590c' },
    { label: t('pageViews'), value: totals.total_page_views || 0, sub: `${(data?.top_pages || []).length} pages`, icon: Eye, color: '#1a1a1a' },
    { label: t('uniqueUsers'), value: totals.unique_users || 0, sub: `DAU ${activeUsers.dau || 0} / WAU ${activeUsers.wau || 0}`, icon: Users, color: '#666666' },
    { label: t('sessions'), value: sessions.total || 0, sub: `~${sessions.avg_pages_per_session || 0} pages/session`, icon: Activity, color: '#e8590c' },
  ];

  const tooltipStyle = { fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' };

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div className="analytics-header">
        <div className="analytics-header-top">
          <span className="analytics-label">MONITORING PLATEFORME</span>
        </div>
        <h1 className="analytics-title">
          <strong>{totals.unique_users || 0}</strong> {t('activeUsersLabel')}, <strong>{totals.total_page_views || 0}</strong> {t('pageViews').toLowerCase()}
        </h1>
        <div className="analytics-header-row">
          <span className="analytics-update">
            <Clock size={13} />
            {t('updatedJustNow')}
          </span>
          <div className="analytics-periods">
            {getPeriods(t).map(p => (
              <button
                key={p.value}
                className={`analytics-period-btn ${period === p.value ? 'analytics-period-btn--active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" style={{ marginBottom: 32 }}>
        {kpis.map(kpi => (
          <div key={kpi.label} className="analytics-kpi">
            <div className="analytics-kpi-label">
              <span className="analytics-kpi-dot" style={{ background: kpi.color }} />
              {kpi.label}
            </div>
            <div className="analytics-kpi-value">{kpi.value}</div>
            <div className="analytics-kpi-delta">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts Row 1: Line charts */}
      <div className="grid md:grid-cols-2 gap-5" style={{ marginBottom: 32 }}>
        {/* Logins over time */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{t('logins')}</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.logins_over_time || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.logins_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke="#e8590c" strokeWidth={2}
                    dot={{ r: 3, fill: '#e8590c', strokeWidth: 0 }} name={t('logins')} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="analytics-empty">{t('noLoginsInPeriod')}</div>
            )}
          </CardContent>
        </Card>

        {/* Page views over time */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{t('pageViews')}</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.page_views_over_time || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.page_views_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke="#1a1a1a" strokeWidth={2}
                    dot={{ r: 3, fill: '#1a1a1a', strokeWidth: 0 }} name={t('pageViews')} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="analytics-empty">{t('noPageViewsInPeriod')}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Top pages bar + Devices/Countries pies */}
      <div className="grid md:grid-cols-2 gap-5" style={{ marginBottom: 32 }}>
        {/* Top pages */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{t('mostVisitedPages')}</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.top_pages || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={(data.top_pages || []).slice(0, 10)} layout="vertical" barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="page" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="views" fill="#1a1a1a" radius={[0, 4, 4, 0]} name={t('views')} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="analytics-empty">{t('noData')}</div>
            )}
          </CardContent>
        </Card>

        {/* Devices pie */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{t('devices')}</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.devices || []).length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={data.devices} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={2} dataKey="count" nameKey="device_type" strokeWidth={0}>
                      {data.devices.map((_, i) => (
                        <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="analytics-legend">
                  {data.devices.map((entry, i) => {
                    const DevIcon = DEVICE_ICONS[entry.device_type] || Monitor;
                    return (
                      <div key={i} className="analytics-legend-item">
                        <span className="analytics-legend-dot" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                        <DevIcon size={14} style={{ marginRight: 4, opacity: 0.6 }} />
                        <span className="analytics-legend-label">{entry.device_type}</span>
                        <span className="analytics-legend-value">{entry.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="analytics-empty">{t('noData')}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3: Countries pie */}
      {(data?.countries || []).length > 0 && (
        <div className="grid md:grid-cols-2 gap-5" style={{ marginBottom: 32 }}>
          <Card className="analytics-card">
            <CardHeader className="analytics-card-header">
              <CardTitle className="analytics-card-title">
                <Globe size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                {t('countries')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={data.countries} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={2} dataKey="count" nameKey="country" strokeWidth={0}>
                      {data.countries.map((_, i) => (
                        <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="analytics-legend">
                  {data.countries.map((entry, i) => (
                    <div key={i} className="analytics-legend-item">
                      <span className="analytics-legend-dot" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                      <span className="analytics-legend-label">{entry.country || t('unknown')}</span>
                      <span className="analytics-legend-value">{entry.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Session stats */}
          <Card className="analytics-card">
            <CardHeader className="analytics-card-header">
              <CardTitle className="analytics-card-title">{t('sessions')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 0' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#1a1a1a' }}>{sessions.total}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{t('totalSessions')}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#e8590c' }}>
                    {sessions.avg_duration_seconds > 60
                      ? `${Math.round(sessions.avg_duration_seconds / 60)}min`
                      : `${sessions.avg_duration_seconds}s`}
                  </div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{t('averageDuration')}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#666' }}>{sessions.avg_pages_per_session}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{t('pagesPerSession')}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#1a1a1a' }}>{activeUsers.mau || 0}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>MAU</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top pages table */}
      {(data?.top_pages || []).length > 0 && (
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{t('pageDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>{t('views')}</th>
                    <th>{t('uniqueVisitors')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_pages.map((page, i) => (
                    <tr key={i}>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 13 }}>{page.page}</span></td>
                      <td><strong>{page.views}</strong></td>
                      <td>{page.unique_visitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </ProfessionalLayout>
  );
};

export default PlatformMonitoringPage;
